<template>
	<div class="reservation-new">
		<ModalHeader :title="getString('new_reservation')" :button-text="getString('save')" :button-loading="loading" :button-disabled="newDate === null" @click-button="addReservation"/>
		<AppList :items="items">
			<template v-slot:date-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<DatePicker ref="date-picker-date" v-model="newDate" mode="date" color="teal" :min-date='now' :max-date="maxDate" :is-dark="userStore.darkMode" :disabled-dates="disabledDates" @update:modelValue="changedDate">
						<template #default="{ togglePopover }">
							<AppTextInput class="text-input ml-auto" @click="togglePopover()" readonly :placeholder="getString('reservation_date')"
										  :value="newDate ? formatDateOnly(newDate, false) : null"/>
						</template>
					</DatePicker>
				</div>
			</template>
			<template v-slot:time-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<AppButton v-if="dailyTimeslots.length > 0" class="ml-auto" outline :text="newTime ? formatTimeHHmm(newTime, false) : getString('time')" :loading="loadingTime" @click="openTimeSelector" :disabled="newDate === null" />
					<p v-else class="no-time-available">{{ getString('no_time_available') }}</p>
				</div>
			</template>
			<template #guests-slot="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<div class="touch-number-input-container">
						<TouchNumberInput :min="1" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
					</div>
				</div>
			</template>
			<template #list-slot="{item}">
				<AppList :items="item.subitems">
					<template #dogs-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
					<template #children-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
					<template #list-slot="{item}">
						<AppList :items="item.subitems"/>
					</template>
				</AppList>
			</template>
		</AppList>
		<ReservationTablesSelect :experience-id="newExperience" :active="editTables" :initial-selected-tables="initialSelectedTables"
								 :reservation-people="newPeople" :reservation-date="newDate" :reservation-time="newTime"
								 :reservation-duration="reservationDuration" @toggling-popup="togglingTables" @confirm-changes="changedTables"/>
		<TimeSelector :active="timeSheetActive" :daily-timeslots="dailyTimeslots" :exceptions="scheduleExceptions" :availability="availability"
					  :max-capacity="maxCapacity" :selected-date="newDate" @toggling-popup="v => timeSheetActive = v"
					  @selected-time="setSelectedTime"/>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import TimeSelector from "@/console-new/components/ops/tables/TimeSelector.vue";
import ReservationTablesSelect from "@/console-new/components/ops/tables/ReservationTablesSelect.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import {useUserStore} from "@/user/stores";
import {formatDateTime, formatDateOnly, formatTimeHHmm, localToServerTime, getLocalDateFromServer} from "@/shared/helpers/dates";
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import {useExperiencesStore, useRoomsOrdersStore, useRoomsStore, useOrdersStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import {timeslotTimes} from "@/book/helpers/availabilities";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const roomsStore = useRoomsStore();
		const ordersStore = useOrdersStore();
		const experiencesStore = useExperiencesStore();
		return {
			getString: languageStore.getString,
			roomsOrdersStore,
			userStore,
			notificationStore,
			roomsStore,
			ordersStore,
			experiencesStore,
			formatDateTime,
			formatDateOnly,
			formatTimeHHmm
		}
	},
	name: "ReservationNew",
	components: {AppTextInput, AppButton, TimeSelector, AppList, ModalHeader, ReservationTablesSelect, TouchNumberInput},
	data() {
		return {
			now: new Date(),
			newDate: new Date(),
			newTime: null,
			loading: false,
			loadingTime: false,
			occasions: [
				'birthday',
				'anniversary',
				'honeymoon',
				'business_reason',
				'graduation',
			],
			newName: null,
			newEmail: null,
			newPhone: null,
			newPeople: null,
			newDogs: null,
			newChildren: null,
			newNotes: null,
			newSpecial: null,
			newExperience: null,
			newTables: null,
			editTables: false,
			timeSheetActive: false,
			scheduleTimeslots: [],
			scheduleExceptions: [],
			availability: [],
			maxCapacity: 0,
			otherExpanded: false,
		}
	},
	computed: {
		dailyTimeslots() {
			if (!this.newDate || !this.scheduleTimeslots || this.scheduleTimeslots.length === 0) {
				return [];
			}
			const dateStr = formatDateOnly(this.newDate);
			const foundException = this.scheduleExceptions.find(ex => formatDateOnly(new Date(ex.date)) === dateStr);
			if (foundException) {
				return foundException.timeslots || [];
			}
			const weekday = this.newDate.getDay();
			let timeslots = this.scheduleTimeslots.filter(t => ((t.weekday + 1) % 7) === weekday);

			let times = [];
			timeslots.forEach(t => {
				let tHours = timeslotTimes(t, this.availability, this.maxCapacity, this.isDateToday);
				if(tHours.length > 0) {
					times.push({slot: t, times: tHours});
				}
			});
			return times;
		},
		isDateToday() {
			return this.newDate && formatDateOnly(new Date()) === formatDateOnly(this.newDate);
		},
		tablesTitle() {
			if (!this.newTables) {
				return this.getString('select_tables');
			}
			if (this.newTables.length <= 0) {
				return this.getString('no_tables');
			} else if (this.newTables.length === 1) {
				return "1 " + this.getString('table');
			} else {
				return this.newTables.length + " " + this.getString('tables');
			}
		},
		initialSelectedTables() {
			let selectedTables = {};
			if (this.newTables && this.newTables.length > 0) {
				this.newTables.forEach(table => {
					selectedTables[table] = true;
				});
			}
			return selectedTables;
		},
		items() {
			return [
				{
					title: this.getString('experience'),
					selectValues: this.experiencesStore.businessExperiences ? this.experiencesStore.businessExperiences.map(e => e.name) : [],
					selectedIndex: this.experiencesStore.businessExperiences ? this.experiencesStore.businessExperiences.findIndex(e => e.experience_id === this.newExperience) : null,
					callbackSelect: (index) => {
						this.newExperience = this.experiencesStore.businessExperiences[index].experience_id;
						if(this.newTime) {
							this.newTime = null;
						}
						if(this.newDate) {
							this.checkDateAvailable();
						}

					},
				},
				{
					title: this.getString('reservation_date'),
					customSlot: 'date-edit',
				},
				{
					title: this.getString('time'),
					customSlot: 'time-edit',
				},
				{
					title: this.getString('guests'),
					customSlot: 'guests-slot',
					value: this.newPeople,
					callbackNumber: (val) => {
						this.newPeople = val;
					},
				},
				{
					title: this.getString('guest_name'),
					textInput: true,
					placeholder: this.getString('guest_name'),
					value: this.newName,
					callbackText: (val) => {
						this.newName = val;
					},
				},
				{
					title: this.getString('email'),
					textInput: true,
					placeholder: this.getString('email'),
					type: 'email',
					value: this.newEmail,
					callbackText: (val) => {
						this.newEmail = val;
					},
				},
				{
					title: this.getString('phone'),
					textInput: true,
					placeholder: this.getString('phone'),
					type: 'tel',
					value: this.newPhone,
					callbackText: (val) => {
						this.newPhone = val;
					},
				},
				{
					title: this.getString('tables'),
					buttonTitle: this.tablesTitle,
					buttonDisabled: !this.newExperience || !this.newTime,
					callbackButton: () => {
						this.editTables = true;
					},
				},
				{
					title: this.getString('other'),
					dropdownSlot: 'list-slot',
					chevronItem: true,
					active: this.otherExpanded,
					callback: (val) => {
						this.otherExpanded = val;
					},
					subitems: [
						{
							title: this.getString('dogs'),
							customSlot: 'dogs-slot',
							value: this.newDogs,
							callbackNumber: (val) => {
								this.newDogs = val;
							},
						},
						{
							title: this.getString('children'),
							customSlot: 'children-slot',
							value: this.newChildren,
							callbackNumber: (val) => {
								this.newChildren = val;
							},
						},
						{
							title: this.getString('special_occasion'),
							dropdownSlot: 'list-slot',
							switch: true,
							active: this.newSpecial !== null,
							callback: (val) => {
								if (val) {
									this.newSpecial = this.occasions[0];
								} else {
									this.newSpecial = null;
								}
							},
							subitems: [
								{
									title: this.getString('special_occasion'),
									selectValues: this.occasions.map(o => this.getString(o)),
									selectedIndex: this.specialIndex,
									callbackSelect: (index) => {
										this.newSpecial = this.occasions[index];
									},
								}
							]
						},
						{
							title: this.getString('notes'),
							textInput: true,
							isTextArea: true,
							placeholder: this.getString('notes'),
							value: this.newNotes || "",
							callbackText: (val) => {
								this.newNotes = val;
							},
						},
					]
				},
			]
		},
		maxDate() {
			let maxDate = new Date();
			maxDate.setDate(maxDate.getDate() + (this.currentExperience && this.currentExperience.days_advance ? this.currentExperience.days_advance : 30));
			return maxDate;
		},
		currentExperience() {
			return this.experiencesStore.businessExperiences
				? this.experiencesStore.businessExperiences.find(e => e.experience_id === this.newExperience)
				: null;
		},
		disabledDates() {
			let result = [];
			if(this.scheduleExceptions) {
				for(let i = 0; i < this.scheduleExceptions.length; i++) {
					let exception = this.scheduleExceptions[i];
					if(!exception.timeslots || exception.timeslots.length === 0) {
						result.push(new Date(exception.date));
					}
				}
			}
			let emptyWeekdays = [0,1,2,3,4,5,6];
			this.scheduleTimeslots.forEach(t => {
				if(t.weekday !== null) {
					emptyWeekdays = emptyWeekdays.filter(d => d !== (t.weekday+1)%7);
				}
			});
			if(emptyWeekdays.length > 0) {
				emptyWeekdays = emptyWeekdays.map(d => d+1);
				result.push({
					repeat: {
						weekdays: emptyWeekdays
					}
				});
			}
			return result;
		},
		specialIndex() {
			let index = this.occasions.indexOf(this.newSpecial || '');
			return index > -1 ? index : 0;
		},
		reservationDuration() {
			if(!this.newTime) return 60;
			let tStr = formatTimeHHmm(this.newTime, false);
			for(let entry of this.dailyTimeslots) {
				if(entry.times.find(tt => tt.time === tStr)) {
					return entry.slot.time || 60;
				}
			}
			return 60;
		}
	},
	methods: {
		checkDateAvailable() {
			if (!this.newDate) return;

			const selectedDate = new Date(this.newDate);
			const selDay = selectedDate.getDay() + 1;

			const isDateDisabled = this.disabledDates.some(disabledEntry => {
				if (disabledEntry instanceof Date) {
					return selectedDate.toDateString() === disabledEntry.toDateString();
				}

				if (disabledEntry.repeat && Array.isArray(disabledEntry.repeat.weekdays)) {
					return disabledEntry.repeat.weekdays.includes(selDay);
				}

				return false;
			});

			if (isDateDisabled) {
				this.newDate = null;
			}
		},
		changedDate() {
			if(this.newTime) {
				this.newTime = null;
			}
		},
		openTimeSelector() {
			this.timeSheetActive = true;
		},
		setSelectedTime(time) {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length >= 2) {
				const d = new Date(this.newDate);
				d.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
				this.newTime = d;
			}
		},
		loadExperienceData() {
			if (!this.newExperience) {
				this.scheduleTimeslots = [];
				this.scheduleExceptions = [];
			}

			const schedulePromise = new Promise((resolve, reject) => {
				API.init().getExperienceSchedule(
					this.userStore.currBusinessId,
					this.newExperience,
					res => resolve(res),
					err => reject(err)
				);
			});

			const exceptionsPromise = new Promise((resolve, reject) => {
				API.init().getExperienceExceptions(
					this.userStore.currBusinessId,
					this.newExperience,
					res => resolve(res),
					err => reject(err)
				);
			});

			this.loadingTime = true;
			Promise.all([schedulePromise, exceptionsPromise])
				.then(([scheduleRes, exceptionsRes]) => {

					this.loadingTime = false;

					this.scheduleTimeslots = scheduleRes?.timeslots
						? scheduleRes.timeslots.map(t =>
							this.experiencesStore.normalizeTimeslot(t)
						)
						: [];

					if (exceptionsRes?.exceptions) {
						const exMap = {};
						exceptionsRes.exceptions_timeslots.forEach(t => {
							if (!t.experience_timeslot_exception_id) return;
							const id = t.experience_timeslot_exception_id;
							(exMap[id] ??= []).push(
								this.experiencesStore.normalizeTimeslot(t)
							);
						});
						this.scheduleExceptions = exceptionsRes.exceptions.map(e => ({
							experience_timeslot_exception_id: Number(
								e.experience_timeslot_exception_id
							),
							date: e.date,
							timeslots: exMap[e.experience_timeslot_exception_id] ?? []
						}));
					} else {
						this.scheduleExceptions = [];
					}

					if(this.newDate) {
						this.checkDateAvailable();
					}
				})
				.catch(err => {
					console.error('Failed to load experience data:', err);
					this.scheduleTimeslots = [];
					this.scheduleExceptions = [];
					this.loadingTime = false;
					throw err;
				});
		},
		loadAvailability() {
			if (!this.newExperience || !this.newDate) {
				this.availability = [];
				return;
			}
			let date = formatDateOnly(this.newDate);
			API.init().getExperienceAvailability(this.userStore.currBusinessId, this.newExperience, date, r => {
				if (r && r.result === 'OK') {

					r.availability.forEach(reservation => {
						let start = getLocalDateFromServer(date + " " + reservation.start);
						let end = getLocalDateFromServer(date + " " + reservation.end);
						reservation.start = formatTimeHHmm(start, false);
						reservation.end = formatTimeHHmm(end, false);
					});
					this.availability = r.availability;
					this.maxCapacity = r.max_capacity;
				} else {
					this.availability = [];
					this.maxCapacity = 0;
				}
			});
		},
		changedTables(selectedTables) {
			if (selectedTables && selectedTables.length > 0) {
				this.newTables = selectedTables;
			}
		},
		togglingTables(v) {
			if (!v) {
				this.editTables = false;
			}
		},
		addReservation() {
			this.loading = true;

			let data = {};
			if (this.newExperience) {
				data["experience_id"] = this.newExperience;
			} else {
				this.loading = false;
				this.notificationStore.showNotification("invalid-experience");
				return;
			}
			if (this.newDate && this.newTime) {
				let combined = new Date(this.newDate);
				combined.setHours(this.newTime.getHours(), this.newTime.getMinutes(), 0, 0);
				let dateServer = localToServerTime(combined);
				data["date"] = dateServer.toISOString().split('T')[0];
				data["time"] = dateServer.toTimeString().split(' ')[0];
			} else {
				this.loading = false;
				this.notificationStore.showNotification(!this.newDate ? "invalid-date" : "invalid-time");
				return;
			}
			if (!this.newPeople || this.newPeople <= 0) {
				this.loading = false;
				this.notificationStore.showNotification("invalid-people");
				return;
			}
			if (this.newName) {
				data["name"] = this.newName;
			} else {
				this.loading = false;
				this.notificationStore.showNotification("invalid-name");
				return;
			}
			if (this.newEmail) {
				if (!validateEmail(this.newEmail)) {
					this.loading = false;
					this.notificationStore.showNotification("wrong-email");
					return;
				}
				data["email"] = this.newEmail;
			}
			if (this.newPhone) {
				if (!validatePhone(this.newPhone)) {
					this.loading = false;
					this.notificationStore.showNotification("wrong-phone");
					return;
				}
				data["phone"] = this.newPhone;
			}
			data["people"] = this.newPeople;
			if (this.newDogs) {
				data["dogs"] = this.newDogs;
			}
			if (this.newChildren) {
				data["children"] = this.newChildren;
			}
			if (this.newNotes) {
				data["notes"] = this.newNotes;
			}
			if (this.newSpecial) {
				data["special"] = this.newSpecial;
			}
			if (this.newTables && this.newTables.length > 0) {
				data["tables"] = this.newTables;
			} else {
				data["tables"] = [];
			}


			API.init().newReservationManual(this.userStore.currBusinessId,
				data, (r) => {
					this.loading = false;
					if (r && r.result && r.result === "OK") {

						this.roomsOrdersStore.manageReservationId = null;


						if (this.roomsOrdersStore.reservationsTablesActive) {
							this.roomsOrdersStore.getDayReservations(() => {
								this.notificationStore.showNotification("generic-success");
								this.ordersStore.tablesToCheck = true;
							});
						} else {
							this.roomsStore.updateRoomStatus(this.roomsStore.activeRoom, (r) => {
								this.notificationStore.showNotification("generic-success");
								this.ordersStore.tablesToCheck = true;
							});
						}


						this.roomsOrdersStore.newReservationTableId = null;

					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				});
		},
	},
	mounted() {
		if (this.roomsOrdersStore.newReservationTableId !== null && this.roomsOrdersStore.newReservationTableId !== true) {
			this.newTables = [this.roomsOrdersStore.newReservationTableId];
		}
		if (this.experiencesStore.businessExperiences && this.experiencesStore.businessExperiences.length > 0) {
			this.newExperience = this.experiencesStore.businessExperiences[0].experience_id;
		}
	},
	watch: {
		newExperience() {
			this.loadExperienceData();
			this.loadAvailability();
		},
		newDate() {
			this.loadAvailability();
		}
	}
}
</script>

<style scoped lang="scss">
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;

	.list-item-title {
		margin-right: 5px;
	}

	.input-wrapper {
		width: 240px;
		margin-left: auto;

		@media (max-width: 1250px) {
			width: 190px;
		}
	}

	.no-time-available {
		font-size: 16px;
		color: var(--color-text-primary);
		background-color: var(--color-bg-primary-2);
		border-radius: 13px;
		padding: 6px 10px;
		min-height: 35px;
		width: fit-content;
	}

	.touch-number-input-container {
		margin-left: auto;
		
		.input-wrapper {
			width: auto !important;
		}
	}

}
</style>